import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppThunk } from '../app/store';
import { selectTimetableForHall } from './timetableSlice';

// Enriched warning pushed by the desktop (chairperson feature). Mirrors
// `EnrichedWarning` in app/src/redux/components/warnings/chairpersonWarningsThunks.ts
// — keep both shapes in sync. All references are pre-resolved to display names on
// the desktop; `ttUuid` is the owning timetable item (for filtering + navigation).
export interface EnrichedWarning {
  uuid: string;
  type: string;
  warning: string;
  participantUuid: string;
  participantName: string;
  adjudicatorUuid: string;
  adjudicatorName: string;
  danceUuid: string;
  danceName: string;
  roundUuid: string;
  ttUuid: string;
  categoryName: string;
  roundName: string;
  timestamp: number;
}

interface WarningsState {
  // Full snapshot of the competition's warnings (replaced on each snapshot).
  warnings: EnrichedWarning[];
  // FIFO of new warnings awaiting a modal alert (handles bursts from many judges).
  queue: EnrichedWarning[];
  // Navigation request raised by the alert's "Review" button. `nonce` makes
  // repeated navigations to the same item re-trigger the consuming effect.
  navTarget: { ttUuid: string; nonce: number } | null;
}

const initialState: WarningsState = {
  warnings: [],
  queue: [],
  navTarget: null,
};

const warningsSlice = createSlice({
  name: 'warnings',
  initialState,
  reducers: {
    setWarnings(state, action: PayloadAction<EnrichedWarning[]>) {
      state.warnings = action.payload ?? [];
    },
    addWarning(state, action: PayloadAction<EnrichedWarning>) {
      const w = action.payload;
      if (!w || !w.uuid) return;
      if (state.warnings.some((x) => x.uuid === w.uuid)) return;
      state.warnings.push(w);
    },
    enqueueAlert(state, action: PayloadAction<EnrichedWarning>) {
      if (action.payload) state.queue.push(action.payload);
    },
    dismissAlert(state) {
      state.queue.shift();
    },
    setNavTarget(
      state,
      action: PayloadAction<{ ttUuid: string; nonce: number }>
    ) {
      state.navTarget = action.payload;
    },
    clearNavTarget(state) {
      state.navTarget = null;
    },
  },
});

export const {
  setWarnings,
  addWarning,
  enqueueAlert,
  dismissAlert,
  setNavTarget,
  clearNavTarget,
} = warningsSlice.actions;

// Store a pushed warning and, only when it belongs to this device's hall, queue a
// modal alert. Cross-hall warnings are stored but never surfaced (the Warnings tab
// filters by the selected in-hall item).
export const receiveNewWarning =
  (warning: EnrichedWarning): AppThunk =>
  (dispatch, getState) => {
    if (!warning || !warning.uuid) return;
    dispatch(addWarning(warning));
    const hallTimetable = selectTimetableForHall(getState());
    const inHall = hallTimetable.some((item) => item.ttUuid === warning.ttUuid);
    if (inHall) {
      dispatch(enqueueAlert(warning));
    }
  };

export const selectAllWarnings = (state: RootState) => state.warnings.warnings;
export const selectAlertQueue = (state: RootState) => state.warnings.queue;
export const selectCurrentAlert = (state: RootState) =>
  state.warnings.queue[0] ?? null;
export const selectWarningsNavTarget = (state: RootState) =>
  state.warnings.navTarget;

export default warningsSlice.reducer;
